import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Schema } from "@bw/shared/types/generated";
import { formatDate } from "@bw/shared/utils";
import { useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { getReport, resolveReport } from "../api";

type Disposition = Schema["ResolveReportRequest"]["disposition"];

const BASE_ACTIONS: Array<{ disposition: Disposition; label: string }> = [
  { disposition: "reviewed_no_action", label: "Reviewed — no action" },
  { disposition: "dismissed", label: "Dismiss report" },
];

const DISPOSITION_LABELS: Record<Disposition, string> = {
  reviewed_no_action: "Reviewed — no action",
  dismissed: "Dismissed",
  remove_post: "Remove post",
  remove_comment: "Remove comment",
};

export function ModerationReportDetailPage() {
  const { reportId = "" } = useParams<{ reportId: string }>();
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<{
    disposition: Disposition;
    label: string;
    targetRevision: string | null;
  } | null>(null);
  const [notes, setNotes] = useState("");
  const [evidenceRefresh, setEvidenceRefresh] = useState<
    "idle" | "refreshing" | "failed"
  >("idle");
  const [recoveryKind, setRecoveryKind] = useState<
    "target_changed" | "already_resolved" | null
  >(null);
  const inFlight = useRef(false);
  const reportQuery = useQuery({
    queryKey: ["platform", "reports", "detail", reportId],
    queryFn: () => getReport(reportId),
    enabled: Boolean(reportId),
  });
  const refreshEvidence = () => {
    setEvidenceRefresh("refreshing");
    void reportQuery.refetch().then((result) => {
      setEvidenceRefresh(result.isError ? "failed" : "idle");
      if (!result.isError) setRecoveryKind(null);
    });
  };
  const resolution = useMutation({
    mutationFn: ({
      disposition,
      targetRevision,
    }: {
      disposition: Disposition;
      targetRevision: string | null;
    }) =>
      resolveReport(
        reportId,
        disposition,
        targetRevision,
        notes.trim() || null,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData<Schema["PlatformReportDetailResponse"]>(
        ["platform", "reports", "detail", reportId],
        (current) =>
          current
            ? {
                ...current,
                status: data.report.status,
                disposition: data.report.disposition,
                target_effect: data.report.target_effect,
                resolution_notes: data.report.resolution_notes,
              }
            : current,
      );
      void queryClient.invalidateQueries({
        queryKey: ["platform", "reports", "detail", reportId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["platform", "reports"],
      });
      setSelection(null);
    },
    onError: (error) => {
      if (!isApiError(error)) return;
      if (error.code === "MODERATION_TARGET_CHANGED") {
        setRecoveryKind("target_changed");
        setSelection(null);
        refreshEvidence();
      } else if (error.code === "REPORT_ALREADY_RESOLVED") {
        setRecoveryKind("already_resolved");
        setSelection(null);
        refreshEvidence();
      }
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });

  const report = reportQuery.data
    ? resolution.data
      ? {
          ...reportQuery.data,
          status: resolution.data.report.status,
          disposition: resolution.data.report.disposition,
          target_effect: resolution.data.report.target_effect,
          resolution_notes: resolution.data.report.resolution_notes,
        }
      : reportQuery.data
    : undefined;
  const targetEffect = resolution.data?.target_effect ?? report?.target_effect;

  if (reportQuery.isLoading) {
    return (
      <PlatformPage title="Report detail">
        <Stack
          role="status"
          aria-label="Loading report"
          sx={{ alignItems: "center", py: 8 }}
        >
          <CircularProgress size={32} />
        </Stack>
      </PlatformPage>
    );
  }
  if (!report) {
    return (
      <PlatformPage title="Report detail">
        <Alert
          severity="error"
          role="alert"
          action={
            <Button color="inherit" onClick={() => void reportQuery.refetch()}>
              Try again
            </Button>
          }
        >
          We couldn&apos;t load this report.
        </Alert>
      </PlatformPage>
    );
  }

  const actions = [...BASE_ACTIONS];
  if (report.target_type === "post" && !report.target_is_deleted) {
    actions.push({ disposition: "remove_post", label: "Remove post" });
  } else if (report.target_type === "comment" && !report.target_is_deleted) {
    actions.push({ disposition: "remove_comment", label: "Remove comment" });
  }
  const needsContentEvidence =
    report.target_type === "post" || report.target_type === "comment";
  const targetMedia = report.target_media ?? [];
  const hasRenderableContent = Boolean(
    report.target_content_preview?.trim() || targetMedia.length > 0,
  );
  const hasDecisionContext =
    !needsContentEvidence ||
    report.target_is_deleted ||
    Boolean(
      hasRenderableContent &&
        report.target_author_display_name?.trim() &&
        report.target_context?.trim(),
    );

  return (
    <>
      <PlatformPage title="Report detail">
        <Stack spacing={3}>
          <Button
            component={RouterLink}
            to="/moderation"
            variant="text"
            sx={{ alignSelf: "flex-start" }}
          >
            Back to reports
          </Button>
          {evidenceRefresh !== "idle" ? (
            <Alert
              severity="warning"
              role="alert"
              action={
                evidenceRefresh === "failed" ? (
                  <Button color="inherit" onClick={refreshEvidence}>
                    Try again
                  </Button>
                ) : undefined
              }
            >
              {evidenceRefresh === "refreshing"
                ? recoveryKind === "already_resolved"
                  ? "This report was resolved elsewhere. Refreshing the confirmed decision…"
                  : "The reported content changed. Refreshing the decision evidence…"
                : recoveryKind === "already_resolved"
                  ? "This report was resolved elsewhere, but the confirmed decision could not be loaded. Try again."
                  : "The reported content changed, but the latest evidence could not be loaded. Try again before resolving it."}
            </Alert>
          ) : null}
          {reportQuery.isRefetchError && evidenceRefresh === "idle" ? (
            <Alert
              severity="warning"
              role="alert"
              action={
                <Button
                  color="inherit"
                  onClick={() => void reportQuery.refetch()}
                >
                  Try again
                </Button>
              }
            >
              {resolution.data
                ? "The decision is saved, but refresh failed. The confirmed result remains shown."
                : "Refresh failed. The last loaded report remains shown."}
            </Alert>
          ) : null}
          {targetEffect ? (
            <Alert severity="success" role="status">
              {targetEffect === "removed"
                ? "Content removed."
                : targetEffect === "already_absent"
                  ? "The reported content was already absent."
                  : "Report resolved with no target change."}
            </Alert>
          ) : null}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ justifyContent: "space-between" }}
              >
                <Typography component="h2" variant="h6">
                  {report.reason}
                </Typography>
                <StatusBadge status={report.status} />
              </Stack>
              <Stack spacing={0.25}>
                <Typography color="text.secondary" variant="overline">
                  Reporter
                </Typography>
                <Typography>{report.reporter_display_name}</Typography>
              </Stack>
              <Stack spacing={0.25}>
                <Typography color="text.secondary" variant="overline">
                  Reported target
                </Typography>
                <Typography>{report.target_display_name}</Typography>
              </Stack>
              {needsContentEvidence ? (
                hasDecisionContext ? (
                  <Stack spacing={1.5}>
                    {report.target_content_preview ? (
                      <Stack spacing={0.25}>
                        <Typography color="text.secondary" variant="overline">
                          Content
                        </Typography>
                        <Typography sx={{ whiteSpace: "pre-wrap" }}>
                          {report.target_content_preview}
                        </Typography>
                      </Stack>
                    ) : null}
                    {targetMedia.length > 0 ? (
                      <Stack spacing={1}>
                        <Typography color="text.secondary" variant="overline">
                          Media evidence
                        </Typography>
                        <Typography color="warning.main" variant="body2">
                          Images are not loaded automatically. Opening media may
                          contact an external site.
                        </Typography>
                        {targetMedia.map((media, index) =>
                          media.media_type === "image" ? (
                            <Button
                              key={`${media.url}-${index}`}
                              component="a"
                              href={media.url}
                              target="_blank"
                              rel="noreferrer"
                              variant="outlined"
                              sx={{ alignSelf: "flex-start" }}
                            >
                              Open reported image {index + 1}
                            </Button>
                          ) : (
                            <Button
                              key={`${media.url}-${index}`}
                              component="a"
                              href={media.url}
                              target="_blank"
                              rel="noreferrer"
                              variant="outlined"
                              sx={{ alignSelf: "flex-start" }}
                            >
                              Open reported video
                            </Button>
                          ),
                        )}
                      </Stack>
                    ) : null}
                    {report.target_author_display_name ? (
                      <Stack spacing={0.25}>
                        <Typography color="text.secondary" variant="overline">
                          Author
                        </Typography>
                        <Typography>
                          {report.target_author_display_name}
                        </Typography>
                      </Stack>
                    ) : null}
                    {report.target_context ? (
                      <Typography color="text.secondary" variant="body2">
                        {report.target_context}
                      </Typography>
                    ) : null}
                    {report.target_deep_link ? (
                      <Button
                        component="a"
                        href={report.target_deep_link}
                        target="_blank"
                        rel="noreferrer"
                        variant="text"
                        sx={{ alignSelf: "flex-start" }}
                      >
                        Open reported content
                      </Button>
                    ) : null}
                    {report.target_is_deleted ? (
                      <Alert severity="info">
                        This content is already unavailable.
                      </Alert>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        Content is available.
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Alert severity="warning" role="alert">
                    Decision context is unavailable. Refresh before resolving
                    this report.
                  </Alert>
                )
              ) : null}
              <Typography color="text.secondary" variant="body2">
                {report.target_type} · {formatDate(report.created_at)}
              </Typography>
              {report.status === "resolved" && report.disposition ? (
                <Stack spacing={0.25}>
                  <Typography color="text.secondary" variant="overline">
                    Disposition
                  </Typography>
                  <Typography>
                    {DISPOSITION_LABELS[report.disposition]}
                  </Typography>
                </Stack>
              ) : null}
              {report.resolution_notes ? (
                <Typography>{report.resolution_notes}</Typography>
              ) : null}
              {report.status === "pending" ? (
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ flexWrap: "wrap" }}
                >
                  {actions.map((action) => (
                    <Button
                      key={action.disposition}
                      variant="outlined"
                      disabled={
                        !hasDecisionContext ||
                        evidenceRefresh !== "idle" ||
                        reportQuery.isFetching
                      }
                      color={
                        action.disposition.startsWith("remove_")
                          ? "error"
                          : "primary"
                      }
                      onClick={() => {
                        resolution.reset();
                        setSelection({
                          ...action,
                          targetRevision: report.target_revision ?? null,
                        });
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </PlatformPage>

      <Dialog
        open={selection !== null}
        onClose={() => !resolution.isPending && setSelection(null)}
        aria-labelledby="report-resolution-title"
      >
        <DialogTitle id="report-resolution-title">
          {selection?.label}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirm this moderation disposition. The report and any target
            change are committed together.
          </DialogContentText>
          <TextField
            label="Resolution notes (optional)"
            fullWidth
            multiline
            minRows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            sx={{ mt: 2 }}
          />
          {resolution.isError ? (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              We couldn&apos;t save this decision. Please try again.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={resolution.isPending}
            onClick={() => setSelection(null)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={
              selection?.disposition.startsWith("remove_") ? "error" : "primary"
            }
            disabled={resolution.isPending || !hasDecisionContext}
            onClick={() => {
              if (!selection || inFlight.current) return;
              inFlight.current = true;
              resolution.mutate({
                disposition: selection.disposition,
                targetRevision: selection.targetRevision,
              });
            }}
          >
            {resolution.isPending
              ? selection?.disposition.startsWith("remove_")
                ? "Removing…"
                : "Saving…"
              : selection?.label}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
